/**
 * PendingApprovalsPanel — Iter 89.
 *
 * Amber alert block listing reservations in `pending_approval` with a live
 * countdown ("Time remaining to respond: 01:59:59") and Accept / Decline
 * actions. Used on both the Vendor and Admin dashboards (admin acts on
 * behalf of the host). Vendors also get the "Require my approval" toggle.
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { AlarmClock, Check, X, Loader2, ShieldAlert } from "lucide-react";

function useNow() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

function fmtLeft(ms) {
  if (ms <= 0) return "00:00:00";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

export default function PendingApprovalsPanel({ bookings = [], scope = "vendor", onChanged }) {
  const now = useNow();
  const [busyId, setBusyId] = useState(null);
  const [settings, setSettings] = useState(null);

  const pending = useMemo(
    () => bookings
      .filter((b) => b.status === "pending_approval")
      .sort((a, b) => String(a.approval_deadline_at || "").localeCompare(String(b.approval_deadline_at || ""))),
    [bookings],
  );

  useEffect(() => {
    if (scope !== "vendor") return;
    api.get("/vendor/approval-settings").then((r) => setSettings(r.data)).catch(() => {});
  }, [scope]);

  const toggleRequire = async (v) => {
    try {
      const { data } = await api.patch("/vendor/approval-settings", { require_approval: v });
      setSettings(data);
      toast.success(v ? "New reservations will wait for your approval" : "Instant booking enabled — no approval needed");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const act = async (b, action) => {
    let reason;
    if (action === "decline") {
      reason = window.prompt("Reason for declining (optional — shared with the customer):") || "";
      if (reason === null) return;
    }
    setBusyId(b.id + action);
    try {
      await api.post(`/bookings/${b.id}/approval/${action}`, action === "decline" ? { reason } : {});
      toast.success(action === "accept" ? "Reservation confirmed ✔" : "Reservation declined — customer refunded");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
      onChanged?.();
    } finally {
      setBusyId(null);
    }
  };

  if (pending.length === 0 && !(scope === "vendor" && settings)) return null;

  return (
    <div className="mb-6" data-testid="pending-approvals-panel">
      {scope === "vendor" && settings && (
        <div className="flex items-center gap-2 mb-3 text-xs text-neutral-600" data-testid="approval-toggle-row">
          <Switch
            checked={!!settings.require_approval}
            onCheckedChange={toggleRequire}
            className="data-[state=checked]:bg-amber-500"
            data-testid="require-approval-toggle"
          />
          <span>
            Require my approval before confirmation
            {settings.require_approval && (
              <span className="text-neutral-400"> · response window {settings.approval_deadline_hours}h (admin-set)</span>
            )}
          </span>
        </div>
      )}

      {pending.length > 0 && (
        <div className="rounded-2xl border-2 border-amber-400 bg-amber-50/70 p-4" data-testid="pending-approvals-alert">
          <div className="flex items-center gap-2 mb-3">
            <ShieldAlert className="h-5 w-5 text-amber-600" />
            <span className="font-semibold text-amber-900 text-sm">
              {pending.length} reservation{pending.length > 1 ? "s" : ""} awaiting {scope === "admin" ? "host " : "your "}approval
            </span>
          </div>
          <div className="space-y-2">
            {pending.map((b) => {
              const left = new Date(b.approval_deadline_at || 0).getTime() - now;
              const urgent = left < 30 * 60 * 1000;
              return (
                <div key={b.id} className="rounded-xl bg-white border border-amber-200 p-3 flex flex-wrap items-center gap-3" data-testid={`pending-approval-${b.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">
                      {b.car ? `${b.car.make} ${b.car.model}` : "Vehicle"} · {String(b.start_date).slice(0, 10)} → {String(b.end_date).slice(0, 10)}
                    </div>
                    <div className="text-xs text-neutral-500 truncate">
                      #{b.id.slice(0, 8).toUpperCase()} · {b.customer?.name || "Customer"} · €{Number(b.amount || 0).toFixed(2)} paid
                      {scope === "admin" && b.car?.vendor?.business_name ? ` · ${b.car.vendor.business_name}` : ""}
                    </div>
                    <div className={`inline-flex items-center gap-1 mt-1 text-xs font-bold tabular-nums ${urgent ? "text-rose-600" : "text-amber-700"}`} data-testid={`approval-countdown-${b.id}`}>
                      <AlarmClock className="h-3.5 w-3.5" /> Time remaining to respond: {fmtLeft(left)}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      size="sm"
                      onClick={() => act(b, "accept")}
                      disabled={!!busyId}
                      className="rounded-full bg-emerald-600 hover:bg-emerald-700"
                      data-testid={`approval-accept-${b.id}`}
                    >
                      {busyId === b.id + "accept" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1" />}
                      Accept
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => act(b, "decline")}
                      disabled={!!busyId}
                      className="rounded-full border-rose-300 text-rose-600 hover:bg-rose-50"
                      data-testid={`approval-decline-${b.id}`}
                    >
                      {busyId === b.id + "decline" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5 mr-1" />}
                      Decline
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
